import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { AlertTriangle, Trash2, Download, CheckCircle } from 'lucide-react';
import axiosInstance from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';

/**
 * Shared "type the company name to confirm" delete modal.
 * Used from the company selection page and from user settings.
 *
 * Export-then-delete (bokföringslagen): deletion requires a current offboarding archive —
 * the company's räkenskapsinformation (SIE, originalunderlag, AI-godkännanden) that the
 * owner must retain for 7 years. Step 1 downloads the archive
 * (GET /api/v1/:companyId/offboarding/export, Owner-only, records the export server-side);
 * step 2 is the existing type-the-name delete (DELETE /api/v1/auth/company/:companyId).
 * The backend refuses deletion (409) without a current export — including when bookkeeping
 * continued after the last export — and that refusal re-opens step 1 here.
 */
// ENABLE THIS WHEN TESTED — hidden per owner decision 2026-08-24: the
// offboarding-export flow failed untested in deployed envs; until verified end
// to end the handover is the USER'S responsibility and deletion does not
// require the archive. Re-enable = flip this to true AND uncomment the gate
// block in backend CompanyService.deleteCompany (+ un-skip its tests).
const OFFBOARDING_EXPORT_STEP_ENABLED = false;

const DeleteCompanyModal = ({ show, onHide, companyId, companyName, onDeleted }) => {
    const [confirmText, setConfirmText] = useState('');
    const [busy, setBusy] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exported, setExported] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!show) {
            setConfirmText('');
            setBusy(false);
            setExporting(false);
            setExported(false);
            setError(null);
        }
    }, [show]);

    const canDelete = confirmText === companyName && (exported || !OFFBOARDING_EXPORT_STEP_ENABLED) && !busy;

    const handleExport = async () => {
        setExporting(true);
        setError(null);
        try {
            const response = await axiosInstance.get(
                getApiUrl(`${companyId}/offboarding/export`),
                { responseType: 'blob', timeout: 300000 },
            );
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = `ledgerloop-arkiv-${(companyName || 'foretag').replace(/[^\w.-]+/g, '_')}.zip`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setExported(true);
        } catch (err) {
            const msg = err?.response?.status === 403
                ? 'Endast företagets ägare kan ladda ner arkivet.'
                : 'Arkivet kunde inte skapas. Försök igen — företaget har inte tagits bort.';
            setError(msg);
        } finally {
            setExporting(false);
        }
    };

    const handleDelete = async () => {
        if (!canDelete) return;
        setBusy(true);
        setError(null);
        try {
            await axiosInstance.delete(getApiUrl(`auth/company/${companyId}`));
            onDeleted?.(companyId);
            onHide?.();
        } catch (err) {
            if (err?.response?.status === 409) {
                // Export missing or stale (bookkeeping continued after it) — re-open step 1.
                setExported(false);
                setError('Arkivet är inte aktuellt — bokföring har tillkommit efter nedladdningen. Ladda ner arkivet igen och försök sedan ta bort företaget.');
            } else {
                const msg = err?.response?.data?.message || 'Kunde inte ta bort företaget.';
                setError(msg);
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <AlertTriangle size={24} style={{ color: '#dc2626' }} />
                    Bekräfta borttagning
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: '2rem' }}>
                <p style={{ fontSize: '1rem', marginBottom: '1rem' }}>
                    Är du säker på att du vill ta bort företaget{' '}
                    <strong style={{ color: '#dc2626' }}>{companyName}</strong>?
                </p>
                <div style={{
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: 6,
                    padding: '1rem',
                    fontSize: '0.875rem',
                    color: '#dc2626',
                    marginBottom: '1.5rem',
                }}>
                    <strong>Varning:</strong> Denna åtgärd kan inte ångras. All data kopplad till detta företag kommer att tas bort permanent.
                </div>
                {OFFBOARDING_EXPORT_STEP_ENABLED && (
                <div style={{
                    background: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: 6,
                    padding: '1rem',
                    fontSize: '0.875rem',
                    color: '#374151',
                    marginBottom: '1.5rem',
                }}>
                    <strong>Steg 1 — Ladda ner bokföringsarkivet.</strong>{' '}
                    Enligt bokföringslagen ansvarar företaget för att bevara sin räkenskapsinformation
                    i 7 år. Arkivet innehåller SIE-filer per räkenskapsår, alla originalunderlag och
                    AI-godkännanden med underlag. Företaget kan inte tas bort utan ett aktuellt arkiv.
                    <div style={{ marginTop: '0.75rem' }}>
                        <Button
                            variant={exported ? 'outline-success' : 'success'}
                            onClick={handleExport}
                            disabled={exporting || busy}
                            style={{ padding: '0.5rem 1rem', background: exported ? undefined : '#44814E', borderColor: '#44814E' }}
                        >
                            {exported
                                ? (<><CheckCircle size={16} style={{ marginRight: '0.5rem' }} />Arkiv nedladdat — ladda ner igen</>)
                                : (<><Download size={16} style={{ marginRight: '0.5rem' }} />{exporting ? 'Skapar arkiv…' : 'Ladda ner arkiv'}</>)}
                        </Button>
                    </div>
                </div>
                )}
                <label style={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem',
                    display: 'block',
                }}>
                    {OFFBOARDING_EXPORT_STEP_ENABLED ? 'Steg 2 — Skriv företagsnamnet för att bekräfta:' : 'Skriv företagsnamnet för att bekräfta:'}
                </label>
                <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={companyName}
                    disabled={busy}
                    style={{
                        width: '100%',
                        padding: '0.75rem',
                        fontSize: '1rem',
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        outline: 'none',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = '#44814E')}
                    onBlur={(e) => (e.target.style.borderColor = '#d1d5db')}
                />
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', marginBottom: 0 }}>
                    Skriv exakt: <strong>{companyName}</strong>
                </p>
                {error && (
                    <p style={{ marginTop: '1rem', color: '#dc2626', fontSize: '0.875rem', marginBottom: 0 }}>
                        {error}
                    </p>
                )}
            </Modal.Body>
            <Modal.Footer style={{ borderTop: '1px solid #e5e7eb', padding: '1rem 2rem' }}>
                <Button variant="secondary" onClick={onHide} disabled={busy} style={{ padding: '0.5rem 1rem' }}>
                    Avbryt
                </Button>
                <Button
                    variant="danger"
                    onClick={handleDelete}
                    disabled={!canDelete}
                    title={OFFBOARDING_EXPORT_STEP_ENABLED && !exported ? 'Ladda ner bokföringsarkivet först (steg 1)' : undefined}
                    style={{
                        padding: '0.5rem 1rem',
                        opacity: canDelete ? 1 : 0.5,
                        cursor: canDelete ? 'pointer' : 'not-allowed',
                    }}
                >
                    <Trash2 size={16} style={{ marginRight: '0.5rem' }} />
                    {busy ? 'Tar bort...' : 'Ta bort företag'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default DeleteCompanyModal;
